/**
 * The diff, and it is the point of the screen.
 *
 * `web/shapes/policies.md` §5. Each line carries its **direction** as icon and text,
 * not as a colour: `DESIGN.md` §4 makes the label mandatory, and this is exactly the
 * kind of list a person skims — a reader who skims a grey list of field changes has
 * learned "what fields changed", which is a question nobody had.
 *
 * Severity here maps to consequence and not to loudness:
 *
 *   `tightens`  → high    — more of somebody's activity starts being recorded.
 *   `loosens`   → low     — less is recorded. Not a warning; a thing that happened.
 *   `judgement` → info    — how time is judged moves; what is collected does not.
 *
 * The acknowledgement checkbox appears **only when something tightens**
 * (`requiresAcknowledgement`), for the reason `web/src/policies/diff.ts` gives at
 * length: a warning that fires on correct work is one whoever it blocks learns to
 * click past, and reclassifying a vendor's website is correct work.
 *
 * Nothing here has been sent. The panel says so in as many words, on every render,
 * because a diff that looks like a receipt is a diff somebody stops reading.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { MessageKey } from '../../i18n/messages';
import type { ChangeDirection, PolicyChange } from '../../policies/diff';
import { requiresAcknowledgement } from '../../policies/diff';
import { Badge } from '../../states/Badge';
import type { IconName, Severity } from '../../states/icons';

const DIRECTION_LOOK: Readonly<
  Record<ChangeDirection, { severity: Severity; icon: IconName; key: MessageKey }>
> = {
  tightens: {
    severity: 'high',
    icon: 'triangle-alert',
    key: 'policies.direction.tightens',
  },
  loosens: { severity: 'low', icon: 'lock', key: 'policies.direction.loosens' },
  judgement: { severity: 'info', icon: 'sliders-horizontal', key: 'policies.direction.judgement' },
};

export interface DiffSummaryProps {
  readonly changes: readonly PolicyChange[];
  /** The existing rule's version, when the save updates one rather than creating one. */
  readonly fromVersion: number | null;
  readonly acknowledged: boolean;
  readonly saving: boolean;
  readonly onAcknowledge: (next: boolean) => void;
  readonly onSave: () => void;
}

export function DiffSummary({
  changes,
  fromVersion,
  acknowledged,
  saving,
  onAcknowledge,
  onSave,
}: DiffSummaryProps): ReactNode {
  const { t } = useI18n();
  const needsAck = requiresAcknowledgement(changes);
  const canSave = changes.length > 0 && (!needsAck || acknowledged);

  return (
    <section className="wp-section" aria-labelledby="k5-diff-heading" data-testid="k5-diff">
      <h2 className="wp-section__title" id="k5-diff-heading">
        {t('policies.diff.title')}
      </h2>
      <p className="wp-small wp-muted" data-testid="k5-diff-unsaved">
        {t('policies.diff.unsaved')}
      </p>

      {changes.length === 0 ? (
        <p className="wp-muted" data-testid="k5-diff-none">
          {t('policies.diff.none')}
        </p>
      ) : (
        <>
          <p data-testid="k5-diff-version">
            {fromVersion === null
              ? t('policies.diff.creating')
              : t('policies.diff.updating', { from: fromVersion, to: fromVersion + 1 })}
          </p>
          <ul className="wp-rows" data-testid="k5-diff-list">
            {changes.map((change) => {
              const look = DIRECTION_LOOK[change.direction];
              return (
                <li
                  key={`${change.field}-${change.host}`}
                  className="wp-row"
                  data-direction={change.direction}
                  data-testid={`k5-diff-${change.field}`}
                >
                  <span className="wp-row__main">
                    {t(change.messageKey, {
                      host: change.host,
                      from: change.from ?? '',
                      to: change.to ?? '',
                    })}
                  </span>
                  <Badge
                    severity={look.severity}
                    icon={look.icon}
                    label={t(look.key)}
                    testId={`k5-direction-${change.field}`}
                  />
                </li>
              );
            })}
          </ul>

          {needsAck ? (
            <>
              <label className="wp-toggle" htmlFor="k5-diff-ack">
                <input
                  id="k5-diff-ack"
                  type="checkbox"
                  checked={acknowledged}
                  aria-describedby="k5-diff-ack-why"
                  onChange={(event) => {
                    onAcknowledge(event.target.checked);
                  }}
                  data-testid="k5-diff-ack"
                />
                <span>{t('policies.ack.label')}</span>
              </label>
              <p className="wp-small wp-muted" id="k5-diff-ack-why">
                {t('policies.ack.why')}
              </p>
            </>
          ) : null}

          {/* Absent rather than disabled, the same ruling `RevokeConfirm` applies:
              the operator has not finished saying what they are doing, which is not
              the same claim as "the system will not let you". */}
          {canSave ? (
            <button
              type="button"
              className="wp-button wp-button--primary"
              onClick={onSave}
              data-testid="k5-save"
            >
              {saving ? t('policies.saving') : t('policies.save')}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
